// src/components/Admin/AdminHeader.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  User, 
  LogOut, 
  Settings, 
  Home, 
  ChevronDown,
  Menu
} from 'lucide-react';
import type { RootState } from '../../store';
import { useAppDispatch } from '../../store';
import { userActions } from '../../store/reducers/userReducers';
import { performCompleteLogout } from '../../utils/authUtils';

interface AdminHeaderProps {
  onToggleSidebar?: () => void;
  isSidebarCollapsed?: boolean;
  isMobile?: boolean;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ 
  onToggleSidebar, 
  isMobile = false 
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  
  const { userInfo } = useSelector((state: RootState) => state.user);

  const handleSignOut = async () => {
    try {
      await performCompleteLogout();
      dispatch(userActions.resetUserInfo());
      navigate('/login');
    } catch (error) {
      // Silently handle logout failure
      console.error('Logout error:', error);
    }
  };

  const avatarUrl = userInfo?.avatar || userInfo?.avatarUrl;

  return (
    <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left Section - Mobile Menu */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Right Section - Actions and Profile */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Quick Actions */}
          {!isMobile && (
            <button
              onClick={() => navigate('/')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Back to Site"
            >
              <Home className="h-5 w-5" />
            </button>
          )}

          {/* Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center">
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt="Profile" 
                      className="h-8 w-8 rounded-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                      }}
                    />
                  ) : (
                    <User className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-700 truncate max-w-32">
                    {userInfo?.firstName} {userInfo?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">Administrator</p>
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{userInfo?.firstName} {userInfo?.lastName}</p>
                  <p className="text-xs text-gray-500">{userInfo?.email}</p>
                </div>
                
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsProfileDropdownOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <User className="h-4 w-4 mr-3" />
                  My Profile
                </button>
                
                <button
                  onClick={() => {
                    navigate('/admin/system/settings');
                    setIsProfileDropdownOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Settings className="h-4 w-4 mr-3" />
                  Admin Settings
                </button>
                
                <div className="border-t border-gray-100 mt-1">
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside handler */}
      {isProfileDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsProfileDropdownOpen(false)}
        />
      )}
    </header>
  );
};

export default AdminHeader;